import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, MoveRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import FormDialog from "../components/FormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { OPPORTUNITY_FIELDS } from "../lib/entities";
import { fmtEUR, fmtDate, initials } from "../lib/format";

export default function Pipeline() {
  const { can } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [pid, setPid] = useState(null);
  const [opps, setOpps] = useState([]);
  const [create, setCreate] = useState(null); // stage_id for quick add
  const dragId = useRef(null);

  const loadPipelines = useCallback(async () => {
    const { data } = await api.get("/pipelines");
    setPipelines(data);
    if (data.length && !pid) setPid(data[0].id);
  }, [pid]);

  const loadOpps = useCallback(async () => {
    if (!pid) return;
    const { data } = await api.get("/opportunities", { params: { pipeline_id: pid } });
    setOpps(data);
  }, [pid]);

  useEffect(() => { loadPipelines(); }, [loadPipelines]);
  useEffect(() => { loadOpps(); }, [loadOpps]);

  const pipeline = pipelines.find((p) => p.id === pid);
  const stages = pipeline?.stages || [];

  const onDrop = async (stageId) => {
    const id = dragId.current;
    dragId.current = null;
    document.querySelectorAll(".drag-over").forEach((el) => el.classList.remove("drag-over"));
    await moveTo(id, stageId);
  };

  const moveTo = async (id, stageId) => {
    const opp = opps.find((o) => o.id === id);
    if (!opp || opp.stage_id === stageId) return;
    setOpps((prev) => prev.map((o) => (o.id === id ? { ...o, stage_id: stageId } : o)));
    try {
      await api.patch(`/opportunities/${id}/stage`, { stage_id: stageId });
      toast.success("Fase aggiornata");
      loadOpps();
    } catch { toast.error("Errore aggiornamento"); loadOpps(); }
  };

  return (
    <div>
      <PageHeader title="Pipeline Visuale" subtitle="Kanban drag & drop · trascina le opportunità tra le fasi (su touch usa il pulsante Sposta sulla card)">
        <Select value={pid || ""} onValueChange={setPid}>
          <SelectTrigger data-testid="pipeline-selector" className="w-full sm:w-64 h-10"><SelectValue placeholder="Pipeline" /></SelectTrigger>
          <SelectContent>{pipelines.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      <div className="flex gap-3 overflow-x-auto pb-4 kanban-scroll snap-x snap-mandatory lg:snap-none -mx-3 px-3 sm:mx-0 sm:px-0" data-testid="kanban-board">
        {stages.map((s) => {
          const stageOpps = opps.filter((o) => o.stage_id === s.id);
          const total = stageOpps.reduce((sum, o) => sum + (o.value || 0), 0);
          return (
            <div
              key={s.id}
              data-testid={`pipeline-stage-column-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag-over"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("drag-over")}
              onDrop={() => onDrop(s.id)}
              className="w-[82vw] max-w-[18rem] sm:w-72 shrink-0 snap-start bg-slate-100/70 rounded-xl flex flex-col max-h-[calc(100dvh-260px)] lg:max-h-[calc(100vh-220px)]"
            >
              <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: s.is_won ? "#059669" : s.is_lost ? "#dc2626" : "#0D9488" }} />
                  <span className="text-sm font-semibold text-slate-700 font-heading">{s.name}</span>
                  <span className="text-xs text-slate-400 font-mono">{stageOpps.length}</span>
                </div>
                {can("write") && <button data-testid={`add-opp-stage-${s.id}`} onClick={() => setCreate(s.id)} className="text-slate-400 hover:text-teal-600"><Plus className="w-4 h-4" /></button>}
              </div>
              <div className="px-2 py-1 text-[11px] font-mono text-slate-500">{fmtEUR(total)}</div>
              <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 kanban-scroll">
                {stageOpps.map((o) => (
                  <div
                    key={o.id}
                    data-testid={`kanban-opportunity-card-${o.id}`}
                    draggable
                    onDragStart={(e) => { dragId.current = o.id; e.currentTarget.classList.add("dragging"); }}
                    onDragEnd={(e) => e.currentTarget.classList.remove("dragging")}
                    className="bg-white rounded-lg border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow border-l-2"
                    style={{ borderLeftColor: s.is_won ? "#059669" : s.is_lost ? "#dc2626" : "#0D9488" }}
                  >
                    <p className="text-sm font-medium text-slate-800 leading-snug">{o.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{o.company?.legal_name || "—"}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-mono font-semibold text-navy-900">{fmtEUR(o.value)}</span>
                      <span className="text-[10px] text-slate-400 font-mono">{o.probability}%</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-50">
                      <span className="text-[10px] text-slate-400">{o.close_date ? fmtDate(o.close_date) : "—"} · {o.days_in_stage}gg</span>
                      <span className="flex items-center gap-1.5">
                        {can("write") && stages.length > 1 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`move-opp-${o.id}`} aria-label="Sposta in un'altra fase" onClick={(e) => e.stopPropagation()} className="lg:hidden icon-btn p-1 rounded-md text-slate-400 hover:text-teal-600 hover:bg-slate-100"><MoveRight className="w-4 h-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              {stages.filter((x) => x.id !== s.id).map((x) => (
                                <DropdownMenuItem key={x.id} data-testid={`move-opp-${o.id}-to-${x.id}`} onClick={() => moveTo(o.id, x.id)} className="cursor-pointer gap-2"><span className="w-2 h-2 rounded-full" style={{ background: x.is_won ? "#059669" : x.is_lost ? "#dc2626" : "#0D9488" }} />{x.name}</DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {o.owner && <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-white text-[9px] font-semibold" style={{ background: o.owner.avatar_color }}>{initials(o.owner.name)}</span>}
                      </span>
                    </div>
                  </div>
                ))}
                {stageOpps.length === 0 && <div className="text-center text-xs text-slate-300 py-6">Trascina qui</div>}
              </div>
            </div>
          );
        })}
      </div>

      {create && (
        <FormDialog open={!!create} onOpenChange={(o) => !o && setCreate(null)} title="Nuova Opportunità" fields={OPPORTUNITY_FIELDS}
          endpoint="/opportunities" initial={{ pipeline_id: pid, stage_id: create }} onSaved={() => { setCreate(null); loadOpps(); }} />
      )}
    </div>
  );
}
